import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGlobalContext } from "../Config";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";
import { TabView, TabPanel } from "primereact/tabview";

const POLITICAS_DIA_HABIL = [
  { label: "Mismo día", value: "MISMO_DIA" },
  { label: "Hábil anterior", value: "ANTERIOR_HABIL" },
  { label: "Hábil siguiente", value: "SIGUIENTE_HABIL" },
];

const EMPRESAS_OPTIONS = [
  { label: "ALTAMIRA", value: 1 },
  { label: "LA SIEMBRA", value: 2 },
];

const DIA_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1),
  value: i + 1,
}));

const DEFAULT_POLITICA = "ANTERIOR_HABIL";

const ConfiguracionCorte = () => {
  const { baseUrl } = useGlobalContext();
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const empresaIdFromQuery = query.get("empresaId")
    ? Number(query.get("empresaId"))
    : null;

  const medidorIdFromQuery = query.get("medidorId")
    ? Number(query.get("medidorId"))
    : null;

  const proveedorIdFromQuery = query.get("proveedorId")
    ? Number(query.get("proveedorId"))
    : null;

  const [tabIndex, setTabIndex] = useState(medidorIdFromQuery ? 1 : 0);

  const [empresaId, setEmpresaId] = useState(empresaIdFromQuery);
  const [proveedorId, setProveedorId] = useState(proveedorIdFromQuery);

  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [guardandoEmpresa, setGuardandoEmpresa] = useState(false);

  const [configEmpresaActual, setConfigEmpresaActual] = useState(null);
  const [historialEmpresa, setHistorialEmpresa] = useState([]);

  const [empresaDiaDesde, setEmpresaDiaDesde] = useState(null);
  const [empresaDiaHasta, setEmpresaDiaHasta] = useState(null);
  const [empresaPoliticaDiaHabil, setEmpresaPoliticaDiaHabil] =
    useState(DEFAULT_POLITICA);
  const [empresaVigenteDesde, setEmpresaVigenteDesde] = useState(new Date());

  const [loadingMedidores, setLoadingMedidores] = useState(false);
  const [guardandoMedidor, setGuardandoMedidor] = useState(false);

  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);

  const [medidoresEmpresa, setMedidoresEmpresa] = useState([]);
  const [medidorId, setMedidorId] = useState(medidorIdFromQuery);

  const [configMedidorActual, setConfigMedidorActual] = useState(null);
  const [historialMedidor, setHistorialMedidor] = useState([]);
  const [configMedidorResuelta, setConfigMedidorResuelta] = useState(null);

  const [medidorDiaDesde, setMedidorDiaDesde] = useState(null);
  const [medidorDiaHasta, setMedidorDiaHasta] = useState(null);
  const [medidorPoliticaDiaHabil, setMedidorPoliticaDiaHabil] =
    useState(DEFAULT_POLITICA);
  const [medidorVigenteDesde, setMedidorVigenteDesde] = useState(new Date());

  const [mensajeError, setMensajeError] = useState("");
  const [mensajeOk, setMensajeOk] = useState("");

  const usuario = sessionStorage.getItem("username");

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: "include",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data?.respuesta === false) {
      throw new Error(data?.mensaje || "Error inesperado");
    }

    return data;
  };

  const limpiarMensajes = () => {
    setMensajeError("");
    setMensajeOk("");
  };

  const getEstadoConfiguracion = (row) => {
    if (!row?.vigenteDesde) {
      return {
        value: "Sin fecha",
        severity: "warning",
      };
    }

    const hoy = new Date();
    const hoySoloFecha = new Date(
      hoy.getFullYear(),
      hoy.getMonth(),
      hoy.getDate(),
    );

    const desde = parseFechaSeguro(row.vigenteDesde);
    const desdeSoloFecha = new Date(
      desde.getFullYear(),
      desde.getMonth(),
      desde.getDate(),
    );

    let hastaSoloFecha = null;

    if (row?.vigenteHasta) {
      const hasta = parseFechaSeguro(row.vigenteHasta);
      hastaSoloFecha = new Date(
        hasta.getFullYear(),
        hasta.getMonth(),
        hasta.getDate(),
      );
    }

    if (hoySoloFecha < desdeSoloFecha) {
      return {
        value: "Futura",
        severity: "info",
      };
    }

    if (hastaSoloFecha && hoySoloFecha > hastaSoloFecha) {
      return {
        value: "Histórica",
        severity: "secondary",
      };
    }

    return {
      value: "Vigente",
      severity: "success",
    };
  };

  const formatDateDMY = (value) => {
    if (!value) return "-";

    const date =
      value instanceof Date
        ? value
        : new Date(typeof value === "string" ? value : String(value));

    if (Number.isNaN(date.getTime())) {
      const s = String(value);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return s;
      const [, y, mo, d] = m;
      return `${d}-${mo}-${y}`;
    }

    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const parseFechaSeguro = (value) => {
    if (!value) return new Date();

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? new Date() : value;
    }

    const s = String(value).trim();

    const onlyDateMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (onlyDateMatch) {
      const [, y, m, d] = onlyDateMatch;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }

    // ISO o cualquier fecha parseable
    const parsed = new Date(s);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  const toIsoDate = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const normalizarDia = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const entero = Math.trunc(n);
    if (entero < 1 || entero > 31) return null;
    return entero;
  };

  const calcularDiasPeriodo = (desde, hasta) => {
    const dDesde = normalizarDia(desde);
    const dHasta = normalizarDia(hasta);

    if (!dDesde || !dHasta) return null;

    return 30 - dDesde + 1 + dHasta;
  };

  const esPeriodoMensualValido = (desde, hasta) => {
    const dDesde = normalizarDia(desde);
    const dHasta = normalizarDia(hasta);

    if (!dDesde || !dHasta) return false;

    if (dDesde < dHasta) return false;

    const diasPeriodo = calcularDiasPeriodo(dDesde, dHasta);
    return diasPeriodo >= 28;
  };

  const ordinalEs = (n) => `${Number(n)}º`;

  const politicaLabel = (politica) => {
    const found = POLITICAS_DIA_HABIL.find((item) => item.value === politica);
    return found?.label || politica || "-";
  };

  const buildRangoPeriodoTexto = (desde, hasta) => {
    const dDesde = normalizarDia(desde);
    const dHasta = normalizarDia(hasta);

    if (!dDesde || !dHasta) return "";

    const diasPeriodo = calcularDiasPeriodo(dDesde, dHasta);

    if (!esPeriodoMensualValido(dDesde, dHasta)) {
      if (dDesde < dHasta) {
        return "Configuración inválida: el día desde debe ser mayor o igual al día hasta.";
      }

      return `Configuración inválida: el periodo calculado tendría ${diasPeriodo} días y debe ser de al menos 28 días.`;
    }

    return `Los periodos se interpretan desde el día ${ordinalEs(
      dDesde,
    )} del mes anterior hasta el día ${ordinalEs(
      dHasta,
    )} del mes actual (${diasPeriodo} días).`;
  };

  const buildPoliticaHint = (politica) => {
    switch (politica) {
      case "MISMO_DIA":
        return "Si el día de término del periodo cae en fin de semana, se mantiene el mismo día.";
      case "ANTERIOR_HABIL":
        return "Si el día de término del periodo cae en fin de semana, se ajusta al día hábil anterior.";
      case "SIGUIENTE_HABIL":
        return "Si el día de término del periodo cae en fin de semana, se ajusta al día hábil siguiente.";
      default:
        return "";
    }
  };

  const estadoBody = (row) => {
    const estado = getEstadoConfiguracion(row);

    return <Tag severity={estado.severity} value={estado.value} />;
  };

  const politicaBody = (row) => (
    <Tag value={politicaLabel(row?.politicaDiaHabil)} severity="info" />
  );

  const rangoBody = (row) => (
    <span>{buildRangoPeriodoTexto(row?.diaDesde, row?.diaHasta)}</span>
  );

  const origenResueltoBody = (config) => {
    if (!config) return <Tag value="Sin configuración" severity="warning" />;

    return (
      <Tag
        value={
          config?.nivel === "MEDIDOR"
            ? "Propia de medidor"
            : "Heredada de empresa-proveedor"
        }
        severity={config?.nivel === "MEDIDOR" ? "success" : "info"}
      />
    );
  };

  const resetFormularioEmpresa = () => {
    setEmpresaDiaDesde(null);
    setEmpresaDiaHasta(null);
    setEmpresaPoliticaDiaHabil(DEFAULT_POLITICA);
    setEmpresaVigenteDesde(new Date());
  };

  const resetFormularioMedidor = () => {
    setMedidorDiaDesde(null);
    setMedidorDiaHasta(null);
    setMedidorPoliticaDiaHabil(DEFAULT_POLITICA);
    setMedidorVigenteDesde(new Date());
  };

  const sincronizarFormularioEmpresa = (config) => {
    if (!config) {
      resetFormularioEmpresa();
      return;
    }

    setEmpresaDiaDesde(config.diaDesde ?? null);
    setEmpresaDiaHasta(config.diaHasta ?? null);
    setEmpresaPoliticaDiaHabil(config.politicaDiaHabil || DEFAULT_POLITICA);
    setEmpresaVigenteDesde(parseFechaSeguro(config.vigenteDesde));
  };

  const sincronizarFormularioMedidor = (config) => {
    if (!config) {
      resetFormularioMedidor();
      return;
    }

    setMedidorDiaDesde(config.diaDesde ?? null);
    setMedidorDiaHasta(config.diaHasta ?? null);
    setMedidorPoliticaDiaHabil(config.politicaDiaHabil || DEFAULT_POLITICA);
    setMedidorVigenteDesde(
      config.vigenteDesde
        ? new Date(`${config.vigenteDesde}T00:00:00`)
        : new Date(),
    );
  };

  const getProveedorIdFromMedidor = (medidor) => {
    if (!medidor) return null;

    return (
      medidor?.proveedor?.id ??
      medidor?.idProveedor ??
      medidor?.proveedorId ??
      null
    );
  };

  const getProveedorNombreFromMedidor = (medidor) => {
    return (
      medidor?.proveedor?.razonSocial ||
      medidor?.proveedor?.nombre ||
      medidor?.nombreProveedor ||
      "-"
    );
  };

  const cargarProveedores = async () => {
    setLoadingProveedores(true);

    try {
      const data = await fetchJson(`${baseUrl}proveedor/buscar`);
      const items = Array.isArray(data?.proveedores) ? data.proveedores : [];
      setProveedores(items);
      return items;
    } catch (error) {
      setProveedores([]);
      setMensajeError(error.message);
      return [];
    } finally {
      setLoadingProveedores(false);
    }
  };

  const cargarConfiguracionEmpresa = async (idEmpresa, idProveedor) => {
    if (!idEmpresa || !idProveedor) return;

    setLoadingEmpresa(true);

    try {
      const [actual, hist] = await Promise.all([
        fetchJson(
          `${baseUrl}configuracion-corte/empresa?empresaId=${idEmpresa}&proveedorId=${idProveedor}`,
        ),
        fetchJson(
          `${baseUrl}configuracion-corte/empresa/historial?empresaId=${idEmpresa}&proveedorId=${idProveedor}`,
        ),
      ]);

      const config = actual?.configuracion ?? null;
      const items = Array.isArray(hist?.historial) ? hist.historial : [];

      setConfigEmpresaActual(config);
      setHistorialEmpresa(items);
      sincronizarFormularioEmpresa(config);
    } catch (error) {
      setConfigEmpresaActual(null);
      setHistorialEmpresa([]);
      resetFormularioEmpresa();
      setMensajeError(error.message);
    } finally {
      setLoadingEmpresa(false);
    }
  };

  const cargarMedidoresEmpresa = async (idEmpresa) => {
    if (!idEmpresa) {
      setMedidoresEmpresa([]);
      return [];
    }

    setLoadingMedidores(true);

    try {
      const data = await fetchJson(
        `${baseUrl}medidor/empresa?idEmpresa=${idEmpresa}`,
      );
      const items = Array.isArray(data?.medidores) ? data.medidores : [];

      const options = items
        .filter((item) => item?.categoria === "MEDIDOR")
        .map((item) => ({
          label: item?.codigo
            ? `${item.codigo}${
                item?.tipo?.descripcion ? ` - ${item.tipo.descripcion}` : ""
              }`
            : `Medidor ${item?.id}`,
          value: item?.id,
          raw: item,
        }));

      setMedidoresEmpresa(options);
      return options;
    } catch (error) {
      setMedidoresEmpresa([]);
      setMensajeError(error.message);
      return [];
    } finally {
      setLoadingMedidores(false);
    }
  };

  const cargarConfiguracionMedidor = async (idEmpresa, idMedidor) => {
    if (!idEmpresa || !idMedidor) return;

    setLoadingMedidores(true);

    try {
      const [actual, hist, resuelta] = await Promise.all([
        fetchJson(
          `${baseUrl}configuracion-corte/medidor?medidorId=${idMedidor}`,
        ),
        fetchJson(
          `${baseUrl}configuracion-corte/medidor/historial?medidorId=${idMedidor}`,
        ),
        fetchJson(
          `${baseUrl}configuracion-corte/resolver?empresaId=${idEmpresa}&medidorId=${idMedidor}`,
        ),
      ]);

      const config = actual?.configuracion ?? null;
      const items = Array.isArray(hist?.historial) ? hist.historial : [];
      const aplicada = resuelta?.configuracion ?? null;

      setConfigMedidorActual(config);
      setHistorialMedidor(items);
      setConfigMedidorResuelta(aplicada);
      sincronizarFormularioMedidor(config || aplicada);
    } catch (error) {
      setConfigMedidorActual(null);
      setHistorialMedidor([]);
      setConfigMedidorResuelta(null);
      resetFormularioMedidor();
      setMensajeError(error.message);
    } finally {
      setLoadingMedidores(false);
    }
  };

  const guardarConfiguracionEmpresa = () => {
    limpiarMensajes();

    const desde = normalizarDia(empresaDiaDesde);
    const hasta = normalizarDia(empresaDiaHasta);

    if (!empresaId) {
      setMensajeError("Seleccione una empresa.");
      return;
    }

    if (!proveedorId) {
      setMensajeError("Seleccione un proveedor.");
      return;
    }

    if (!desde) {
      setMensajeError("Debe seleccionar un día desde válido entre 1 y 31.");
      return;
    }

    if (!hasta) {
      setMensajeError("Debe seleccionar un día hasta válido entre 1 y 31.");
      return;
    }

    if (!esPeriodoMensualValido(desde, hasta)) {
      const diasPeriodo = calcularDiasPeriodo(desde, hasta);

      setMensajeError(
        diasPeriodo < 28
          ? `La configuración no es válida. El periodo calculado tendría ${diasPeriodo} días y debe ser de al menos 28 días.`
          : "La configuración debe representar un ciclo mensual. El día desde debe ser mayor o igual al día hasta.",
      );
      return;
    }

    if (!empresaPoliticaDiaHabil) {
      setMensajeError("Debe seleccionar una política de día hábil.");
      return;
    }

    const vigenteDesdeIso = toIsoDate(empresaVigenteDesde);
    if (!vigenteDesdeIso) {
      setMensajeError("Debe seleccionar una fecha de vigencia válida.");
      return;
    }

    confirmDialog({
      message:
        "Se creará una nueva versión de configuración para este par empresa-proveedor. ¿Desea continuar?",
      header: "Confirmar guardado",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Guardar",
      rejectLabel: "Cancelar",
      accept: async () => {
        setGuardandoEmpresa(true);
        try {
          await fetchJson(`${baseUrl}configuracion-corte/empresa`, {
            method: "POST",
            body: JSON.stringify({
              empresaId,
              proveedorId,
              diaDesde: desde,
              diaHasta: hasta,
              politicaDiaHabil: empresaPoliticaDiaHabil,
              vigenteDesde: vigenteDesdeIso,
              usuario,
            }),
          });

          setMensajeOk(
            "Configuración empresa-proveedor guardada correctamente.",
          );
          await cargarConfiguracionEmpresa(empresaId, proveedorId);

          if (medidorId) {
            await cargarConfiguracionMedidor(empresaId, medidorId);
          }
        } catch (error) {
          setMensajeError(error.message);
        } finally {
          setGuardandoEmpresa(false);
        }
      },
    });
  };

  const guardarConfiguracionMedidor = () => {
    limpiarMensajes();

    const desde = normalizarDia(medidorDiaDesde);
    const hasta = normalizarDia(medidorDiaHasta);

    if (!empresaId) {
      setMensajeError("Seleccione una empresa.");
      return;
    }

    if (!medidorId) {
      setMensajeError("Seleccione un medidor.");
      return;
    }

    if (!desde) {
      setMensajeError("Debe seleccionar un día desde válido entre 1 y 31.");
      return;
    }

    if (!hasta) {
      setMensajeError("Debe seleccionar un día hasta válido entre 1 y 31.");
      return;
    }

    if (desde < hasta) {
      setMensajeError(
        "La configuración debe representar un ciclo mensual. El día desde debe ser mayor o igual al día hasta.",
      );
      return;
    }

    if (!esPeriodoMensualValido(desde, hasta)) {
      const diasPeriodo = calcularDiasPeriodo(desde, hasta);

      setMensajeError(
        diasPeriodo < 28
          ? `La configuración no es válida. El periodo calculado tendría ${diasPeriodo} días y debe ser de al menos 28 días.`
          : "La configuración debe representar un ciclo mensual. El día desde debe ser mayor o igual al día hasta.",
      );
      return;
    }

    const vigenteDesdeIso = toIsoDate(medidorVigenteDesde);
    if (!vigenteDesdeIso) {
      setMensajeError("Debe seleccionar una fecha de vigencia válida.");
      return;
    }

    confirmDialog({
      message:
        "Se creará una nueva versión de configuración para este medidor. Los remarcadores asociados heredarán esta configuración de forma indirecta.",
      header: "Confirmar guardado",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Guardar",
      rejectLabel: "Cancelar",
      accept: async () => {
        setGuardandoMedidor(true);
        try {
          await fetchJson(`${baseUrl}configuracion-corte/medidor`, {
            method: "POST",
            body: JSON.stringify({
              empresaId,
              medidorId,
              diaDesde: desde,
              diaHasta: hasta,
              politicaDiaHabil: medidorPoliticaDiaHabil,
              vigenteDesde: vigenteDesdeIso,
              usuario,
            }),
          });

          setMensajeOk("Configuración de medidor guardada correctamente.");
          await cargarConfiguracionMedidor(empresaId, medidorId);
        } catch (error) {
          setMensajeError(error.message);
        } finally {
          setGuardandoMedidor(false);
        }
      },
    });
  };

  useEffect(() => {
    const nextEmpresaId = empresaIdFromQuery || null;
    const nextMedidorId = medidorIdFromQuery || null;
    const nextProveedorId = proveedorIdFromQuery || null;

    if (nextEmpresaId !== empresaId) {
      setEmpresaId(nextEmpresaId);
    }

    if (nextMedidorId !== medidorId) {
      setMedidorId(nextMedidorId);
    }

    if (nextProveedorId !== proveedorId) {
      setProveedorId(nextProveedorId);
    }

    if (nextMedidorId) {
      setTabIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaIdFromQuery, medidorIdFromQuery, proveedorIdFromQuery]);

  useEffect(() => {
    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    limpiarMensajes();

    if (!empresaId) {
      setProveedorId(proveedorIdFromQuery || null);
      setConfigEmpresaActual(null);
      setHistorialEmpresa([]);
      setMedidoresEmpresa([]);
      setMedidorId(medidorIdFromQuery || null);
      setConfigMedidorActual(null);
      setHistorialMedidor([]);
      setConfigMedidorResuelta(null);
      resetFormularioEmpresa();
      resetFormularioMedidor();
      return;
    }

    const init = async () => {
      const medidores = await cargarMedidoresEmpresa(empresaId);

      const medidorQueryExiste = medidorIdFromQuery
        ? medidores.some((m) => m.value === medidorIdFromQuery)
        : false;

      if (medidorQueryExiste) {
        setMedidorId(medidorIdFromQuery);
        setTabIndex(1);

        const medidorQuery = medidores.find(
          (m) => m.value === medidorIdFromQuery,
        )?.raw;
        const proveedorDesdeMedidor = getProveedorIdFromMedidor(medidorQuery);

        if (!proveedorIdFromQuery && proveedorDesdeMedidor) {
          setProveedorId(proveedorDesdeMedidor);
        }
      } else if (!medidorIdFromQuery) {
        setMedidorId(null);
        setConfigMedidorActual(null);
        setHistorialMedidor([]);
        setConfigMedidorResuelta(null);
        resetFormularioMedidor();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  useEffect(() => {
    limpiarMensajes();

    if (!empresaId || !proveedorId) {
      setConfigEmpresaActual(null);
      setHistorialEmpresa([]);
      resetFormularioEmpresa();
      return;
    }

    cargarConfiguracionEmpresa(empresaId, proveedorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, proveedorId]);

  useEffect(() => {
    limpiarMensajes();

    if (!empresaId || !medidorId) {
      setConfigMedidorActual(null);
      setHistorialMedidor([]);
      setConfigMedidorResuelta(null);
      resetFormularioMedidor();
      return;
    }

    const medidor =
      medidoresEmpresa.find((m) => m.value === medidorId)?.raw || null;
    const proveedorDesdeMedidor = getProveedorIdFromMedidor(medidor);

    if (proveedorDesdeMedidor && proveedorDesdeMedidor !== proveedorId) {
      setProveedorId(proveedorDesdeMedidor);
    }

    cargarConfiguracionMedidor(empresaId, medidorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, medidorId]);

  const resumenEmpresa = useMemo(() => {
    const desde = normalizarDia(empresaDiaDesde);
    const hasta = normalizarDia(empresaDiaHasta);

    if (!desde || !hasta) return null;

    return {
      rango: buildRangoPeriodoTexto(desde, hasta),
      politica: buildPoliticaHint(empresaPoliticaDiaHabil),
    };
  }, [empresaDiaDesde, empresaDiaHasta, empresaPoliticaDiaHabil]);

  const resumenMedidor = useMemo(() => {
    const desde = normalizarDia(medidorDiaDesde);
    const hasta = normalizarDia(medidorDiaHasta);

    if (!desde || !hasta) return null;

    return {
      rango: buildRangoPeriodoTexto(desde, hasta),
      politica: buildPoliticaHint(medidorPoliticaDiaHabil),
    };
  }, [medidorDiaDesde, medidorDiaHasta, medidorPoliticaDiaHabil]);

  const medidorSeleccionado =
    medidorId != null
      ? medidoresEmpresa.find((m) => m.value === medidorId)?.raw || null
      : null;

  const proveedorOptions = proveedores.map((p) => ({
    label: p?.razonSocial
      ? `${p.razonSocial}${p?.rut ? ` - ${p.rut}` : ""}`
      : `Proveedor ${p?.id}`,
    value: p?.id,
    raw: p,
  }));

  const proveedorSeleccionado =
    proveedorId != null
      ? proveedorOptions.find((p) => p.value === proveedorId)?.raw || null
      : null;

  return (
    <div className="col-12">
      <ConfirmDialog />

      {mensajeError ? <Message severity="error" text={mensajeError} /> : null}
      {mensajeOk ? (
        <div style={{ marginTop: mensajeError ? 10 : 0, marginBottom: 10 }}>
          <Message severity="success" text={mensajeOk} />
        </div>
      ) : null}

      <Card title="Configuración de corte">
        <div className="grid">
          <div className="col-12 md:col-4">
            <label>Empresa</label>
            <Dropdown
              value={empresaId}
              options={EMPRESAS_OPTIONS}
              onChange={(e) => setEmpresaId(e.value)}
              placeholder="Seleccione empresa"
              style={{ width: "100%" }}
              disabled={
                loadingEmpresa ||
                loadingMedidores ||
                loadingProveedores ||
                guardandoEmpresa ||
                guardandoMedidor
              }
              showClear
            />
          </div>

          <div className="col-12 md:col-4">
            <label>Proveedor</label>
            <Dropdown
              value={proveedorId}
              options={proveedorOptions}
              onChange={(e) => setProveedorId(e.value)}
              placeholder="Seleccione proveedor"
              style={{ width: "100%" }}
              disabled={
                !empresaId ||
                loadingEmpresa ||
                loadingMedidores ||
                loadingProveedores ||
                guardandoEmpresa ||
                guardandoMedidor
              }
              filter
              showClear
            />
          </div>

          {empresaIdFromQuery || medidorIdFromQuery || proveedorIdFromQuery ? (
            <div className="col-12">
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  borderRadius: 8,
                  background: "#f8f9fa",
                  color: "#495057",
                }}
              >
                <strong>Medidor Seleccionado Desde Inventario</strong>
                <div style={{ marginTop: 8 }}>
                  Empresa ID: {empresaIdFromQuery || "-"} | Proveedor ID:{" "}
                  {proveedorIdFromQuery || "-"} | Medidor ID:{" "}
                  {medidorIdFromQuery || "-"}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <Divider />

        <TabView
          activeIndex={tabIndex}
          onTabChange={(e) => setTabIndex(e.index)}
        >
          <TabPanel header="Empresa-Proveedor">
            <div className="grid">
              <div className="col-12 md:col-3">
                <label>Día desde</label>
                <Dropdown
                  value={empresaDiaDesde}
                  options={DIA_OPTIONS}
                  onChange={(e) => setEmpresaDiaDesde(normalizarDia(e.value))}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleccione o escriba"
                  style={{ width: "100%" }}
                  disabled={
                    !empresaId ||
                    !proveedorId ||
                    loadingEmpresa ||
                    guardandoEmpresa
                  }
                  editable
                  filter
                  showClear
                />
              </div>

              <div className="col-12 md:col-3">
                <label>Día hasta</label>
                <Dropdown
                  value={empresaDiaHasta}
                  options={DIA_OPTIONS}
                  onChange={(e) => setEmpresaDiaHasta(normalizarDia(e.value))}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleccione o escriba"
                  style={{ width: "100%" }}
                  disabled={
                    !empresaId ||
                    !proveedorId ||
                    loadingEmpresa ||
                    guardandoEmpresa
                  }
                  editable
                  filter
                  showClear
                />
              </div>

              <div className="col-12 md:col-3">
                <label>Política día hábil</label>
                <Dropdown
                  value={empresaPoliticaDiaHabil}
                  options={POLITICAS_DIA_HABIL}
                  onChange={(e) => setEmpresaPoliticaDiaHabil(e.value)}
                  placeholder="Seleccione política"
                  style={{ width: "100%" }}
                  disabled={
                    !empresaId ||
                    !proveedorId ||
                    loadingEmpresa ||
                    guardandoEmpresa
                  }
                />
              </div>

              <div className="col-12 md:col-3">
                <label>Vigente desde</label>
                <Calendar
                  value={empresaVigenteDesde}
                  onChange={(e) => setEmpresaVigenteDesde(e.value)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ width: "100%" }}
                  inputStyle={{ width: "100%" }}
                  disabled={
                    !empresaId ||
                    !proveedorId ||
                    loadingEmpresa ||
                    guardandoEmpresa
                  }
                />
              </div>

              <div className="col-12">
                <Button
                  label="Guardar nueva versión empresa-proveedor"
                  icon="pi pi-save"
                  onClick={guardarConfiguracionEmpresa}
                  disabled={
                    !empresaId ||
                    !proveedorId ||
                    loadingEmpresa ||
                    guardandoEmpresa
                  }
                  loading={guardandoEmpresa}
                />
              </div>

              <div className="col-12">
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    background: "#f8f9fa",
                    color: "#495057",
                  }}
                >
                  <strong>Vista previa de la regla</strong>
                  <div style={{ marginTop: 8 }}>
                    {resumenEmpresa ? (
                      <>
                        <div>{resumenEmpresa.rango}</div>
                        <div style={{ marginTop: 6, color: "#6c757d" }}>
                          {resumenEmpresa.politica}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: "#6c757d" }}>
                        Selecciona empresa, proveedor, un rango de días y una
                        política para visualizar el comportamiento de la
                        configuración.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12">
                {loadingEmpresa ? (
                  <div style={{ textAlign: "center", marginTop: 20 }}>
                    <ProgressSpinner />
                  </div>
                ) : null}
              </div>

              <div className="col-12">
                <Card title="Configuración vigente de empresa-proveedor">
                  {configEmpresaActual ? (
                    <div className="grid">
                      <div className="col-12 md:col-3">
                        <strong>Proveedor</strong>
                        <div>
                          {proveedorSeleccionado?.razonSocial ||
                            proveedorSeleccionado?.nombre ||
                            configEmpresaActual?.proveedorId ||
                            "-"}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Día desde</strong>
                        <div>{configEmpresaActual.diaDesde}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Día hasta</strong>
                        <div>{configEmpresaActual.diaHasta}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Política día hábil</strong>
                        <div>
                          {politicaLabel(configEmpresaActual.politicaDiaHabil)}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Versión</strong>
                        <div>{configEmpresaActual.version}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Vigente desde</strong>
                        <div>
                          {formatDateDMY(configEmpresaActual.vigenteDesde)}
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          style={{
                            marginTop: 10,
                            padding: 10,
                            borderRadius: 8,
                            background: "#f8f9fa",
                            color: "#495057",
                            fontSize: 13,
                          }}
                        >
                          <div>
                            {buildRangoPeriodoTexto(
                              configEmpresaActual.diaDesde,
                              configEmpresaActual.diaHasta,
                            )}
                          </div>
                          <div style={{ marginTop: 6, color: "#6c757d" }}>
                            {buildPoliticaHint(
                              configEmpresaActual.politicaDiaHabil,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Message
                      severity="warn"
                      text="El par empresa-proveedor seleccionado aún no tiene una configuración de corte vigente."
                    />
                  )}
                </Card>
              </div>

              <div className="col-12">
                <Card title="Historial de configuraciones empresa-proveedor">
                  <DataTable
                    value={historialEmpresa}
                    stripedRows
                    emptyMessage="No hay configuraciones registradas para el par empresa-proveedor seleccionado."
                    paginator={historialEmpresa.length > 10}
                    rows={10}
                  >
                    <Column field="version" header="Versión" />
                    <Column field="proveedorId" header="Proveedor ID" />
                    <Column field="diaDesde" header="Día desde" />
                    <Column field="diaHasta" header="Día hasta" />
                    <Column header="Rango" body={rangoBody} />
                    <Column header="Política día hábil" body={politicaBody} />
                    <Column
                      field="vigenteDesde"
                      header="Vigente desde"
                      body={(row) => formatDateDMY(row?.vigenteDesde)}
                    />
                    <Column
                      field="vigenteHasta"
                      header="Vigente hasta"
                      body={(row) => formatDateDMY(row?.vigenteHasta)}
                    />
                    <Column header="Estado" body={estadoBody} />
                    <Column
                      field="usuario"
                      header="Modificado por"
                      body={(row) => row?.usuario || "-"}
                    />
                  </DataTable>
                </Card>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Medidores">
            <div className="grid">
              <div className="col-12">
                <Card title="Medidores de la empresa">
                  {!empresaId ? (
                    <Message
                      severity="info"
                      text="Seleccione una empresa para cargar sus medidores."
                    />
                  ) : (
                    <DataTable
                      value={medidoresEmpresa.map((m) => m.raw)}
                      selectionMode="single"
                      selection={medidorSeleccionado}
                      onSelectionChange={(e) => {
                        const medidor = e.value;
                        setMedidorId(medidor?.id || null);

                        const proveedorDesdeMedidor =
                          getProveedorIdFromMedidor(medidor);
                        if (proveedorDesdeMedidor) {
                          setProveedorId(proveedorDesdeMedidor);
                        }
                      }}
                      dataKey="id"
                      stripedRows
                      paginator={medidoresEmpresa.length > 10}
                      rows={10}
                      loading={loadingMedidores}
                      emptyMessage="No hay medidores disponibles para la empresa seleccionada."
                      responsiveLayout="scroll"
                    >
                      <Column
                        field="id"
                        header="ID"
                        style={{ width: "90px" }}
                      />
                      <Column field="codigo" header="Código" />
                      <Column
                        header="Proveedor"
                        body={(row) => getProveedorNombreFromMedidor(row)}
                      />
                      <Column
                        header="Tipo"
                        body={(row) => row?.tipo?.descripcion || "-"}
                      />
                      <Column field="ubicacion" header="Ubicación" />
                      <Column
                        header="Estado"
                        body={(row) => (
                          <Tag
                            value={row?.activo ? "Activo" : "Inactivo"}
                            severity={row?.activo ? "success" : "danger"}
                          />
                        )}
                      />
                    </DataTable>
                  )}
                </Card>
              </div>

              <div className="col-12">
                <Card title="Detalle del medidor seleccionado">
                  {!medidorId ? (
                    <Message
                      severity="info"
                      text="Seleccione un medidor desde la tabla para revisar o editar su configuración."
                    />
                  ) : (
                    <div className="grid">
                      <div className="col-12 md:col-3">
                        <strong>ID</strong>
                        <div>{medidorSeleccionado?.id || "-"}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Código</strong>
                        <div>{medidorSeleccionado?.codigo || "-"}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Proveedor</strong>
                        <div>
                          {getProveedorNombreFromMedidor(medidorSeleccionado)}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Tipo</strong>
                        <div>
                          {medidorSeleccionado?.tipo?.descripcion || "-"}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Ubicación</strong>
                        <div>{medidorSeleccionado?.ubicacion || "-"}</div>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              <div className="col-12">
                <Card title="Configuración efectiva del medidor">
                  {!medidorId ? (
                    <Message
                      severity="info"
                      text="Seleccione un medidor para revisar su configuración efectiva."
                    />
                  ) : loadingMedidores ? (
                    <div style={{ textAlign: "center", marginTop: 20 }}>
                      <ProgressSpinner />
                    </div>
                  ) : configMedidorResuelta ? (
                    <div className="grid">
                      <div className="col-12 md:col-3">
                        <strong>Origen</strong>
                        <div style={{ marginTop: 5 }}>
                          {origenResueltoBody(configMedidorResuelta)}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Día desde</strong>
                        <div>{configMedidorResuelta.diaDesde}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Día hasta</strong>
                        <div>{configMedidorResuelta.diaHasta}</div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Versión</strong>
                        <div>{configMedidorResuelta.version}</div>
                      </div>

                      <div className="col-12 md:col-3">
                        <strong>Política día hábil</strong>
                        <div>
                          {politicaLabel(
                            configMedidorResuelta.politicaDiaHabil,
                          )}
                        </div>
                      </div>
                      <div className="col-12 md:col-3">
                        <strong>Vigente desde</strong>
                        <div>
                          {formatDateDMY(configMedidorResuelta.vigenteDesde)}
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          style={{
                            marginTop: 10,
                            padding: 10,
                            borderRadius: 8,
                            background: "#f8f9fa",
                            color: "#495057",
                            fontSize: 13,
                          }}
                        >
                          <div>
                            {buildRangoPeriodoTexto(
                              configMedidorResuelta.diaDesde,
                              configMedidorResuelta.diaHasta,
                            )}
                          </div>
                          <div style={{ marginTop: 6, color: "#6c757d" }}>
                            {buildPoliticaHint(
                              configMedidorResuelta.politicaDiaHabil,
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <Message
                          severity="info"
                          text="Si el medidor no tiene configuración propia, heredará la configuración del par empresa-proveedor asociado a su proveedor."
                        />
                      </div>
                    </div>
                  ) : (
                    <Message
                      severity="warn"
                      text="No existe configuración efectiva para el medidor seleccionado."
                    />
                  )}
                </Card>
              </div>

              <div className="col-12">
                <Card title="Nueva configuración propia para el medidor">
                  {!medidorId ? (
                    <Message
                      severity="info"
                      text="Seleccione un medidor para crear o actualizar su configuración propia."
                    />
                  ) : (
                    <div className="grid">
                      <div className="col-12 md:col-3">
                        <label>Día desde</label>
                        <Dropdown
                          value={medidorDiaDesde}
                          options={DIA_OPTIONS}
                          onChange={(e) =>
                            setMedidorDiaDesde(normalizarDia(e.value))
                          }
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Seleccione o escriba"
                          style={{ width: "100%" }}
                          disabled={
                            !empresaId ||
                            !medidorId ||
                            loadingMedidores ||
                            guardandoMedidor
                          }
                          editable
                          filter
                          showClear
                        />
                      </div>

                      <div className="col-12 md:col-3">
                        <label>Día hasta</label>
                        <Dropdown
                          value={medidorDiaHasta}
                          options={DIA_OPTIONS}
                          onChange={(e) =>
                            setMedidorDiaHasta(normalizarDia(e.value))
                          }
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Seleccione o escriba"
                          style={{ width: "100%" }}
                          disabled={
                            !empresaId ||
                            !medidorId ||
                            loadingMedidores ||
                            guardandoMedidor
                          }
                          editable
                          filter
                          showClear
                        />
                      </div>

                      <div className="col-12 md:col-3">
                        <label>Política día hábil</label>
                        <Dropdown
                          value={medidorPoliticaDiaHabil}
                          options={POLITICAS_DIA_HABIL}
                          onChange={(e) => setMedidorPoliticaDiaHabil(e.value)}
                          placeholder="Seleccione política"
                          style={{ width: "100%" }}
                          disabled={
                            !empresaId ||
                            !medidorId ||
                            loadingMedidores ||
                            guardandoMedidor
                          }
                        />
                      </div>

                      <div className="col-12 md:col-3">
                        <label>Vigente desde</label>
                        <Calendar
                          value={medidorVigenteDesde}
                          onChange={(e) => setMedidorVigenteDesde(e.value)}
                          dateFormat="dd/mm/yy"
                          showIcon
                          style={{ width: "100%" }}
                          inputStyle={{ width: "100%" }}
                          disabled={
                            !empresaId ||
                            !medidorId ||
                            loadingMedidores ||
                            guardandoMedidor
                          }
                        />
                      </div>

                      <div className="col-12">
                        <Button
                          label="Guardar nueva versión medidor"
                          icon="pi pi-save"
                          onClick={guardarConfiguracionMedidor}
                          disabled={
                            !empresaId ||
                            !medidorId ||
                            loadingMedidores ||
                            guardandoMedidor
                          }
                          loading={guardandoMedidor}
                        />
                      </div>

                      <div className="col-12">
                        <div
                          style={{
                            padding: 12,
                            borderRadius: 8,
                            background: "#f8f9fa",
                            color: "#495057",
                          }}
                        >
                          <strong>Vista previa de la regla</strong>
                          <div style={{ marginTop: 8 }}>
                            {resumenMedidor ? (
                              <>
                                <div>{resumenMedidor.rango}</div>
                                <div style={{ marginTop: 6, color: "#6c757d" }}>
                                  {resumenMedidor.politica}
                                </div>
                              </>
                            ) : (
                              <span style={{ color: "#6c757d" }}>
                                Selecciona un rango de días y una política para
                                visualizar el comportamiento de la configuración
                                del medidor.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              <div className="col-12">
                <Card title="Historial de configuraciones propias del medidor">
                  <DataTable
                    value={historialMedidor}
                    stripedRows
                    emptyMessage="Este medidor no tiene configuraciones propias registradas. Si aparece una configuración efectiva, es heredada desde empresa-proveedor."
                    paginator={historialMedidor.length > 10}
                    rows={10}
                  >
                    <Column field="version" header="Versión" />
                    <Column field="diaDesde" header="Día desde" />
                    <Column field="diaHasta" header="Día hasta" />
                    <Column header="Rango" body={rangoBody} />
                    <Column header="Política día hábil" body={politicaBody} />
                    <Column
                      field="vigenteDesde"
                      header="Vigente desde"
                      body={(row) => formatDateDMY(row?.vigenteDesde)}
                    />
                    <Column
                      field="vigenteHasta"
                      header="Vigente hasta"
                      body={(row) => formatDateDMY(row?.vigenteHasta)}
                    />
                    <Column header="Estado" body={estadoBody} />
                    <Column
                      field="usuario"
                      header="Modificado por"
                      body={(row) => row?.usuario || "-"}
                    />
                  </DataTable>
                </Card>
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
};

export default ConfiguracionCorte;
